import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { celebrationModalService } from './celebration-modal-service';
import styles from './style.module.scss';
import { UIButton } from 'shared/ui';
import ConfettiExplosion from 'react-confetti-explosion';

type CelebrationModalData = {
  header: string;
  headerIcon?: string;
  subtitle: string;
  image: string;
  description: string;
  subDescription?: string;
  buttonText: string;
  showConfetti?: boolean;
  onButtonClick?: () => void;
};

type ModalContextType = {
  showCelebrationModal: (data: CelebrationModalData) => void;
  hideCelebrationModal: () => void;
};

const ModalContext = createContext<ModalContextType>({
  showCelebrationModal: () => {},
  hideCelebrationModal: () => {},
});

export const useCelebrationModal = () => useContext(ModalContext);

type ModalProviderProps = {
  children: ReactNode;
};

export const CelebrationModalProvider = ({ children }: ModalProviderProps) => {
  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    data: CelebrationModalData | null;
  }>({
    isOpen: false,
    data: null,
  });

  const showCelebrationModal = (data: CelebrationModalData) => {
    setModalData({
      isOpen: true,
      data,
    });
  };

  const hideCelebrationModal = () => {
    setModalData({
      isOpen: false,
      data: null,
    });
  };

  useEffect(() => {
    celebrationModalService.init(showCelebrationModal, hideCelebrationModal);
  }, []);

  return (
    <ModalContext.Provider value={{ showCelebrationModal, hideCelebrationModal }}>
      {children}
      {modalData.isOpen && modalData.data && (
        <CelebrationModal data={modalData.data} onClose={hideCelebrationModal} />
      )}
    </ModalContext.Provider>
  );
};

const CelebrationModal = ({
  data,
  onClose,
}: {
  data: CelebrationModalData;
  onClose: () => void;
}) => {
  const handleButtonClick = () => {
    if (data.onButtonClick) {
      data.onButtonClick();
    }
    onClose();
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Header Section */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {data.headerIcon && <span className={styles.headerIcon}>{data.headerIcon}</span>}
            {data.header}
          </h2>
          <p className={styles.subtitle}>{data.subtitle}</p>
        </div>

        {/* Image Section */}
        <div className={styles.imageSection}>
          <img src={data.image} alt="" className={styles.mainImage} />
          {data.showConfetti && (
            <div className={styles.confettiWrapper}>
              <ConfettiExplosion
                zIndex={999999999}
                particleCount={150}
                duration={3000}
                force={0.8}
                width={600}
                colors={['#6ACEFE', '#FE49D8', '#FFC440', '#00FFD1', '#FF00FF']}
              />
            </div>
          )}
        </div>

        {/* Description Section */}
        <div className={styles.descriptionSection}>
          <p className={styles.description}>{data.description}</p>
          {data.subDescription && <p className={styles.subDescription}>{data.subDescription}</p>}
        </div>

        {/* Button */}
        <UIButton onClick={handleButtonClick} className={styles.actionButton}>
          {data.buttonText}
        </UIButton>
      </div>
    </div>
  );
};
